import React, { useEffect, useState } from "react";
import { ShieldCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { PermessiEditor } from "./PermessiEditor";

// Dialog per impostare permessi + PIN di un operatore.
// operatore: { id, nome, cognome, permissions, pin }
export default function OperatorePermDialog({ operatore, open, onOpenChange, onSaved }) {
  const [perms, setPerms] = useState({});
  const [pin, setPin] = useState("");
  const [saving, setSaving] = useState(false);
  const [defaults, setDefaults] = useState([]);

  useEffect(() => {
    if (operatore) {
      setPerms(operatore.permissions || {});
      setPin(String(operatore.pin || ""));
      setDefaults(operatore.default_sections || []);
      api.get(`/manage/operatore-default-sections/${operatore.id}`)
        .then((r) => setDefaults(r.data.default_sections || []))
        .catch(() => {});
    }
  }, [operatore]);

  const save = async () => {
    if (pin && !/^\d{2}$|^\d{4}$/.test(pin)) return toast.error("Il PIN deve avere 2 o 4 cifre");
    setSaving(true);
    try {
      await api.put(`/manage/operatore-permessi/${operatore.id}`, { permissions: perms, pin });
      toast.success("Accessi aggiornati");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally { setSaving(false); }
  };

  if (!operatore) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck size={18} /> Accessi di {operatore.cognome} {operatore.nome}</DialogTitle>
          <DialogDescription>Imposta il PIN e le aree/categorie che l'operatore vedrà entrando con la Firma Rapida.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div className="rounded-xl border p-3 bg-emerald-50/50">
            <Label className="text-xs font-semibold mb-1 flex items-center gap-1.5"><KeyRound size={14} /> PIN Firma Rapida</Label>
            <div className="flex items-center gap-2">
              <Input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                maxLength={4} placeholder="2 o 4 cifre" className="w-28 text-center font-bold tabnum" data-testid="op-perm-pin" />
              <span className="text-[11px] text-muted-foreground">
                PIN a 2 cifre (o 4 per maggiore sicurezza). Vale anche per l'Amministrazione.
              </span>
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold mb-2 block">Aree e categorie visibili a questo operatore</Label>
            <p className="text-[11px] text-muted-foreground mb-2">Quando l'operatore entra col suo PIN vede le sezioni <b>base</b> (grigie) più gli <b>extra</b> che abiliti. Togliendo la spunta a una base la <b>blocchi</b> per questo operatore (badge rosso).</p>
            <PermessiEditor value={perms} onChange={setPerms} testidPrefix="op-perm" defaults={defaults} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="op-perm-save" className="font-semibold">{saving ? "Salvataggio…" : "Salva accessi"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
